import React from 'react';
import { Ticket, Plus, Trash2, Edit2, X, Check, Armchair } from 'lucide-react';
import GlassyDateTimePicker from '../../../components/GlassyDateTimePicker';

const TicketingStep = ({
  ticketTypes,
  ticketFormData,
  showTicketForm,
  editingTicket,
  errors,
  sampleTicketCategories,
  formData,
  onToggleTicketForm,
  onTicketFormChange,
  onAddTicketType,
  onEditTicketType,
  onDeleteTicketType,
  onCancelTicketForm,
  handleInputChange
}) => (
  <div className="form-section">
    <h3><Ticket size={24} /> Ticket Types & Pricing</h3>
    <p>Configure your ticket types to maximize accessibility and revenue!</p>

    <div className="form-field required" style={{ marginBottom: '30px', background: 'rgba(255,255,255,0.03)', padding: '20px', borderRadius: '12px' }}>
      <label><Armchair size={14} /> Seating Mode</label>
      <select
        value={formData.seatingMode}
        onChange={(e) => handleInputChange('seatingMode', e.target.value)}
        className={`wizard-select ${errors.seatingMode ? 'error' : ''}`}
      >
        <option value="">Select Seating Mode</option>
        <option value="UNSEATED">Unseated (Standing/Dancing)</option>
        <option value="SEATED">Seated (Traditional Theater Style)</option>
        <option value="MIXED">Mixed (Both Seated & Standing Areas)</option>
      </select>
      {errors.seatingMode && <div className="error-message">{errors.seatingMode}</div>}
      <div className="hint-text">
        This determines available ticket options. "Seated" events require a chart in next step.
      </div>
    </div>

    {errors.ticketTypes && <div className="error-message">{errors.ticketTypes}</div>}

    <button
      type="button"
      className={`add-ticket-button ${showTicketForm ? 'hide' : ''}`}
      onClick={onToggleTicketForm}
    >
      <Plus size={20} /> Add New Ticket Type
    </button>

    {showTicketForm && (
      <div className="ticket-form-container">
        <h4>{editingTicket ? 'Edit' : 'Create'} Ticket Type</h4>

        {errors.ticketForm && <div className="error-message">{errors.ticketForm}</div>}

        <div className="form-grid">
          <div className="form-field required">
            <label>Category</label>
            <input
              type="text"
              list="category-suggestions"
              value={ticketFormData.category}
              onChange={(e) => onTicketFormChange('category', e.target.value)}
              placeholder="e.g. Early Bird, VIP, Student Discount"
              maxLength={100}
              className="wizard-input"
            />
            <datalist id="category-suggestions">
              {sampleTicketCategories.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>
            <div className={`character-counter ${ticketFormData.category.length > 80 ? 'warning' : ''}`}>
              {ticketFormData.category.length}/100 characters
            </div>
          </div>

          <div className="form-field required">
            <label>Code</label>
            <input
              type="text"
              value={ticketFormData.code}
              onChange={(e) => onTicketFormChange('code', e.target.value)}
              placeholder="e.g. EB, VIP, STU"
              maxLength={20}
              className="wizard-input"
            />
            <div className={`character-counter ${ticketFormData.code.length > 15 ? 'warning' : ''}`}>
              {ticketFormData.code.length}/20 characters
            </div>
          </div>
        </div>

        <div className="form-field required">
          <label>Name</label>
          <input
            type="text"
            value={ticketFormData.name}
            onChange={(e) => onTicketFormChange('name', e.target.value)}
            placeholder="e.g. Early Bird General Admission"
            maxLength={255}
            className="wizard-input"
          />
          <div className={`character-counter ${ticketFormData.name.length > 200 ? 'warning' : ''}`}>
            {ticketFormData.name.length}/255 characters
          </div>
        </div>

        <div className="form-field">
          <label>Description</label>
          <textarea
            value={ticketFormData.description}
            onChange={(e) => onTicketFormChange('description', e.target.value)}
            placeholder="Describe the perks that come with this ticket"
            maxLength={500}
            className="wizard-textarea"
          />
          <div className={`character-counter ${ticketFormData.description.length > 450 ? 'warning' : ''}`}>
            {ticketFormData.description.length}/500 characters
          </div>
        </div>

        <div className="form-grid">
          <div className="form-field required">
            <label>Price</label>
            <input
              type="number"
              value={ticketFormData.priceAmount}
              onChange={(e) => onTicketFormChange('priceAmount', e.target.value)}
              placeholder="35.00"
              className="wizard-input"
            />
          </div>

          <div className="form-field required">
            <label>Currency</label>
            <select
              value={ticketFormData.currencyCode}
              onChange={(e) => onTicketFormChange('currencyCode', e.target.value)}
              className="wizard-select"
            >
              <option value="USD">🇺🇸 USD (US Dollar)</option>
              <option value="EUR">🇪🇺 EUR (Euro)</option>
              <option value="GBP">🇬🇧 GBP (British Pound)</option>
              <option value="CAD">🇨🇦 CAD (Canadian Dollar)</option>
              <option value="AUD">🇦🇺 AUD (Australian Dollar)</option>
              <option value="JPY">🇯🇵 JPY (Japanese Yen)</option>
              <option value="CHF">🇨🇭 CHF (Swiss Franc)</option>
              <option value="CNY">🇨🇳 CNY (Chinese Yuan)</option>
              <option value="INR">🇮🇳 INR (Indian Rupee)</option>
              <option value="BRL">🇧🇷 BRL (Brazilian Real)</option>
              <option value="MXN">🇲🇽 MXN (Mexican Peso)</option>
              <option value="KRW">🇰🇷 KRW (South Korean Won)</option>
              <option value="RUB">🇷🇺 RUB (Russian Ruble)</option>
              <option value="ZAR">🇿🇦 ZAR (South African Rand)</option>
              <option value="TRY">🇹🇷 TRY (Turkish Lira)</option>
              <option value="SAR">🇸🇦 SAR (Saudi Riyal)</option>
              <option value="AED">🇦🇪 AED (UAE Dirham)</option>
              <option value="SGD">🇸🇬 SGD (Singapore Dollar)</option>
              <option value="HKD">🇭🇰 HKD (Hong Kong Dollar)</option>
              <option value="NZD">🇳🇿 NZD (New Zealand Dollar)</option>
              <option value="SEK">🇸🇪 SEK (Swedish Krona)</option>
              <option value="NOK">🇳🇴 NOK (Norwegian Krone)</option>
              <option value="DKK">🇩🇰 DKK (Danish Krone)</option>
              <option value="PLN">🇵🇱 PLN (Polish Zloty)</option>
              <option value="CZK">🇨🇿 CZK (Czech Koruna)</option>
              <option value="HUF">🇭🇺 HUF (Hungarian Forint)</option>
              <option value="RON">🇷🇴 RON (Romanian Leu)</option>
              <option value="BGN">🇧🇬 BGN (Bulgarian Lev)</option>
              <option value="ISK">🇮🇸 ISK (Icelandic Króna)</option>
              <option value="RSD">🇷🇸 RSD (Serbian Dinar)</option>
              <option value="UAH">🇺🇦 UAH (Ukrainian Hryvnia)</option>
            </select>
          </div>

          <div className="form-field">
            <label>Capacity</label>
            <input
              type="number"
              value={ticketFormData.capacity || ''}
              onChange={(e) => onTicketFormChange('capacity', e.target.value)}
              placeholder="Leave empty for unlimited"
              className="wizard-input"
            />
          </div>
        </div>

        <div className="form-field">
          <label>Sales End Date (Optional)</label>
          <GlassyDateTimePicker
            value={ticketFormData.validUntil}
            onChange={(isoDate) => onTicketFormChange('validUntil', isoDate)}
            placeholder="Select sales end date..."
          />
          <div className="hint-text">Tickets will automatically stop being sold after this date.</div>
        </div>

        <div className="form-field required">
          <label>Seat Assignment</label>
          <select
            value={ticketFormData.seatAssignment}
            onChange={(e) => {
              const val = e.target.value;
              onTicketFormChange('seatAssignment', val);
              onTicketFormChange('isSeated', val === 'ASSIGNED');
            }}
            className="wizard-select"
          >
            {formData.seatingMode === 'UNSEATED' && (
              <option value="STANDING">🕺 Standing (Unassigned)</option>
            )}
            {formData.seatingMode === 'SEATED' && (
              <>
                <option value="OPEN_SEATING">🪑 Open Seating (Unassigned)</option>
                <option value="ASSIGNED">🎯 Assigned Seats</option>
              </>
            )}
            {formData.seatingMode === 'MIXED' && (
              <>
                <option value="OPEN_SEATING">🪑 Open Seating (Unassigned)</option>
                <option value="STANDING">🕺 Standing (Unassigned)</option>
                <option value="ASSIGNED">🎯 Assigned Seats</option>
              </>
            )}
          </select>
        </div>

        <div className="form-section" style={{ marginTop: '20px', padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>💰 Group Deals</h4>
          <p style={{ margin: '0 0 16px 0', fontSize: '0.9rem', opacity: 0.7 }}>
            Offer discounted rates for bulk purchases. Perfect for groups, teams, or corporate events.
          </p>

          <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
            <input
              type="checkbox"
              id="groupPackageAvailable"
              checked={ticketFormData.groupPackageAvailable || false}
              onChange={(e) => onTicketFormChange('groupPackageAvailable', e.target.checked)}
              style={{ width: '20px', height: '20px', accentColor: '#3b82f6' }}
            />
            <label htmlFor="groupPackageAvailable" style={{ margin: 0, cursor: 'pointer' }}>
              Enable Group Discount
            </label>
          </div>

          {ticketFormData.groupPackageAvailable && (
            <>
              <div className="form-grid" style={{ marginTop: '16px' }}>
                <div className="form-field required">
                  <label>Minimum Tickets Required</label>
                  <input
                    type="number"
                    min="2"
                    value={ticketFormData.groupMinQuantity || ''}
                    onChange={(e) => onTicketFormChange('groupMinQuantity', parseInt(e.target.value) || '')}
                    placeholder="e.g., 5"
                    className="wizard-input"
                  />
                  <small style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '4px' }}>
                    Minimum number of tickets to qualify for group discount
                  </small>
                </div>

                <div className="form-field required">
                  <label>Discounted Price per Ticket</label>
                  <input
                    type="number"
                    step="0.01"
                    value={ticketFormData.groupUnitPrice || ''}
                    onChange={(e) => onTicketFormChange('groupUnitPrice', e.target.value)}
                    placeholder="e.g., 30.00"
                    className="wizard-input"
                  />
                  <small style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '4px' }}>
                    Price per ticket when buying {ticketFormData.groupMinQuantity || 'minimum'} or more
                  </small>
                </div>
              </div>

              {ticketFormData.priceAmount && ticketFormData.groupUnitPrice && parseFloat(ticketFormData.groupUnitPrice) < parseFloat(ticketFormData.priceAmount) && (
                <div className="wizard-callout success" style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    background: '#2e7d32',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}>
                    Save {Math.round(((parseFloat(ticketFormData.priceAmount) - parseFloat(ticketFormData.groupUnitPrice)) / parseFloat(ticketFormData.priceAmount)) * 100)}%
                  </span>
                  <span style={{ fontSize: '0.9rem', color: '#fff' }}>
                    ${(parseFloat(ticketFormData.priceAmount) - parseFloat(ticketFormData.groupUnitPrice)).toFixed(2)} off per ticket
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="ticket-form-actions">
          <button
            type="button"
            className="ticket-action-button primary"
            onClick={onAddTicketType}
          >
            {editingTicket ? <><Check size={18} /> Update Ticket Type</> : <><Plus size={18} /> Add Ticket Type</>}
          </button>
          <button
            type="button"
            className="ticket-action-button secondary"
            onClick={onCancelTicketForm}
          >
            <X size={18} /> Cancel
          </button>
        </div>
      </div>
    )}

    <div>
      {ticketTypes.map((ticket) => (
        <div key={ticket.id} className="ticket-type-card">
          <div className="ticket-card-header">
            <h4>🎫 {ticket.name}</h4>
            <div className="ticket-price-badge">{ticket.currencyCode || 'USD'} ${ticket.priceAmount}</div>
          </div>
          <div className="ticket-card-details">
            <p><strong>🏷️ Category:</strong> {ticket.category}</p>
            <p><strong>🔖 Code:</strong> {ticket.code}</p>
            {ticket.description && (
              <p><strong>📝 Description:</strong> {ticket.description}</p>
            )}
            {ticket.capacity && (
              <p><strong>👥 Capacity:</strong> {ticket.capacity} tickets</p>
            )}
            {ticket.validUntil && (
              <p><strong>⏰ Sales End:</strong> {new Date(ticket.validUntil).toLocaleString()}</p>
            )}
            <p><strong>💺 Seating:</strong> {
              ticket.seatAssignment === 'ASSIGNED'
                ? '🎯 Assigned Seating'
                : ticket.seatAssignment === 'OPEN_SEATING'
                  ? '🪑 Open Seating'
                  : ticket.seatAssignment === 'STANDING'
                    ? '🕺 Unseated'
                    : ticket.isSeated
                      ? '🪑 Open Seating'
                      : '🕺 Unseated'
            }</p>
            <p><strong>💰 Currency:</strong> {ticket.currencyCode || 'USD'}</p>
          </div>
          <div className="ticket-card-actions">
            <button className="ticket-card-btn edit" onClick={() => onEditTicketType(ticket)}>
              <Edit2 size={16} /> Edit
            </button>
            <button className="ticket-card-btn delete" onClick={() => onDeleteTicketType(ticket.id)}>
              <Trash2 size={16} /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  </div >
);

export default TicketingStep;
